import React, { useEffect } from 'react';
import { Helmet } from 'react-helmet';
import { connect } from 'react-redux';

import { compose } from 'ramda';
import { PublicPageLayout } from 'src/common/components/public-page-layout/public-page-layout';
import { withProviders } from 'src/common/hocs/with-providers';
import type { RootState, AppDispatch } from 'src/redux';

import './blog-item.css';
import { BlogPostSkeleton } from './components/blog-post-skeleton/blog-post-skeleton';
import { BlogPost } from './components/blog-post/blog-post';
import { NotFound } from './components/not-found/not-found';
import { SocialSharing } from './components/social-sharing/social-sharing';
import { fetchBlogPostItem } from './state/blog-item-actions';
import { selectIsLoading, selectPostBySlug } from './state/blog-item-selectors';

type BlogItemComponentOwnProps = {
  params: {
    postSlug: string;
  };
};

type BlogItemComponentProps = BlogItemComponentOwnProps & {
  isLoading: ReturnType<typeof selectIsLoading>;
  post: ReturnType<typeof selectPostBySlug>;
  fetchBlogPostItem: (postSlug: string) => void;
};

const BlogItemComponent = ({
  post,
  params,
  isLoading,
  fetchBlogPostItem,
}: BlogItemComponentProps) => {
  const postUrl =
    post && !isLoading && !post.isError
      ? `https://streamable.com/blog/${post.category.slug}/${post.slug}`
      : undefined;

  useEffect(() => {
    fetchBlogPostItem(params.postSlug);
  }, [fetchBlogPostItem, params.postSlug]);

  return (
    <PublicPageLayout>
      <Helmet>
        <title>{window._STM_PREDATA.blog.title}</title>
        <meta
          name="description"
          content={post ? post.summary : window._STM_PREDATA.blog.description}
        />
        <meta property="og:url" content={postUrl} />
        <meta property="og:type" content="article" />
        <meta property="og:title" content={post && post.title} />
        <meta property="og:description" content={post && post.summary} />
        <meta property="og:image" content={post && post.coverImage?.url} />
        <link rel="canonical" href={postUrl} />
      </Helmet>

      <section className="blog-item">
        <div className="blog-item__content">
          {isLoading ? (
            <BlogPostSkeleton />
          ) : post !== undefined && !post.isError ? (
            <>
              <BlogPost post={post} />
              {postUrl && <SocialSharing url={postUrl} />}
            </>
          ) : (
            <NotFound />
          )}
        </div>
      </section>
    </PublicPageLayout>
  );
};

const mapStateToProps = (
  state: RootState,
  { params }: BlogItemComponentOwnProps
) => ({
  isLoading: selectIsLoading(state, params.postSlug),
  post: selectPostBySlug(state, params.postSlug),
});

const mapDispatchToProps = (dispatch: AppDispatch) => ({
  fetchBlogPostItem: (postSlug: string) =>
    dispatch(fetchBlogPostItem(postSlug)),
});

export const BlogItem = compose(
  connect(mapStateToProps, mapDispatchToProps),
  withProviders({ isDarkModeSupported: false })
)(BlogItemComponent);
